import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/store/auth.store'

/**
 * Runs once on startup: if a user + refresh token survived a reload but the
 * in-memory access token is gone, silently re-authenticate before rendering the
 * app. Blocks render with a minimal splash only while that attempt is in flight,
 * so a refresh doesn't briefly flash the login screen for a valid session.
 */
export function AuthBootstrap({ children }: { children: ReactNode }) {
  const { attemptSilentRefresh } = useAuth()
  const needsRefresh = useAuthStore(
    (s) => Boolean(s.user) && Boolean(s.refreshToken) && !s.accessToken,
  )
  const [ready, setReady] = useState(!needsRefresh)

  useEffect(() => {
    if (ready) return
    let active = true
    void attemptSilentRefresh().finally(() => {
      if (active) setReady(true)
    })
    return () => {
      active = false
    }
    // Run once on mount; attemptSilentRefresh is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-border-subtle border-t-accent" />
      </div>
    )
  }

  return <>{children}</>
}
